/**
 * Universal Pricing Engine Admin Panel.
 *
 * Two-column layout:
 *   • LEFT  ~ 65%: rules table + create/edit modal
 *   • RIGHT ~ 35%: sticky live simulator (calls POST /api/pricing/quote
 *     on every change so the admin can validate rules before saving).
 *
 * The panel is supplier-agnostic: filters (connector, brand, category,
 * style, sku, color, size, customer_category_id, min_quantity) are all
 * optional and empty = wildcard. This means one rule can target ALL
 * suppliers or one specific SKU on Company Casuals — same shape either way.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import PricingRuleFormModal from "@/components/pricing/PricingRuleFormModal";
import PricingSimulator from "@/components/pricing/PricingSimulator";

const AXIOS_OPTS = { withCredentials: true };

function StrategyBadge({ strategy }) {
  const colors = {
    markup: "bg-blue-100 text-blue-900 border-blue-300",
    quantity_break: "bg-amber-100 text-amber-900 border-amber-300",
    fixed_price: "bg-purple-100 text-purple-900 border-purple-300",
    formula: "bg-emerald-100 text-emerald-900 border-emerald-300",
    passthrough: "bg-slate-100 text-slate-900 border-slate-300",
  };
  return (
    <span
      className={`inline-block font-mono-tech text-[0.65rem] font-bold tracking-wider px-2 py-0.5 border ${colors[strategy] || colors.passthrough}`}
    >
      {strategy}
    </span>
  );
}

function FilterChips({ applies_to }) {
  const entries = Object.entries(applies_to || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (entries.length === 0) {
    return <span className="text-[0.7rem] italic text-[var(--text-secondary)]">— (aplica a todo)</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => (
        <span
          key={k}
          className="font-mono-tech text-[0.6rem] font-bold tracking-wider px-1.5 py-0.5 bg-black text-white"
        >
          {k}={String(v)}
        </span>
      ))}
    </div>
  );
}

export default function PricingEnginePanel({ onBack }) {
  const { t } = useI18n();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // rule being edited (null=none, {}=new)
  const [modalOpen, setModalOpen] = useState(false);
  // Master catalog data used by the modal + simulator (dropdowns)
  const [meta, setMeta] = useState({
    strategies: [],
    connectors: [],
    brands: [],
    categories: [],
    colors: [],
    sizes: [],
    customer_categories: [],
  });

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pricing/rules`, AXIOS_OPTS);
      setRules(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando reglas");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const [strat, srcs, brands, cats, colors, sizes, ccats] = await Promise.all([
        axios.get(`${API}/pricing/strategies`, AXIOS_OPTS),
        axios.get(`${API}/catalog/sources`, AXIOS_OPTS).catch(() => ({ data: { tree: [] } })),
        axios.get(`${API}/brands`, AXIOS_OPTS).catch(() => ({ data: [] })),
        axios.get(`${API}/categories`, AXIOS_OPTS).catch(() => ({ data: [] })),
        axios.get(`${API}/colors`, AXIOS_OPTS).catch(() => ({ data: [] })),
        axios.get(`${API}/sizes`, AXIOS_OPTS).catch(() => ({ data: [] })),
        axios.get(`${API}/customer-categories`, AXIOS_OPTS).catch(() => ({ data: [] })),
      ]);
      const connectors = [];
      const tree = strat.data?.strategies ? [] : [];
      // Extract connector slugs from /catalog/sources tree (Purchasing Center leaves).
      const flatten = (nodes) => {
        (nodes || []).forEach((n) => {
          if (n.connector) connectors.push({ slug: n.connector, label: n.label || n.connector });
          if (n.children) flatten(n.children);
        });
      };
      flatten(srcs.data?.tree || []);
      setMeta({
        strategies: strat.data?.strategies || [],
        connectors,
        brands: (brands.data || []).filter((b) => b.is_active !== false),
        categories: (cats.data || []).filter((c) => c.is_active !== false),
        colors: (colors.data || []).filter((c) => c.is_active !== false),
        sizes: (sizes.data || []).filter((s) => s.is_active !== false),
        customer_categories: (ccats.data || []).filter((c) => c.is_active !== false),
      });
    } catch (e) {
      // Non-blocking — modal can still work with plain text inputs.
      console.warn("meta load failed", e);
    }
  }, []);

  useEffect(() => {
    loadRules();
    loadMeta();
  }, [loadRules, loadMeta]);

  const openNew = () => {
    setEditing({});
    setModalOpen(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSaved = () => {
    closeModal();
    loadRules();
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(t("pricing_confirm_delete"))) return;
    try {
      await axios.delete(`${API}/pricing/rules/${rule.id}`, AXIOS_OPTS);
      toast.success("Regla eliminada");
      loadRules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const handleToggle = async (rule) => {
    try {
      // Simple toggle: PUT the full rule with `is_active` flipped.
      const { id, created_at, updated_at, ...payload } = rule;
      await axios.put(
        `${API}/pricing/rules/${rule.id}`,
        { ...payload, is_active: !rule.is_active },
        AXIOS_OPTS,
      );
      loadRules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const sortedRules = useMemo(() => {
    return [...rules].sort((a, b) => {
      if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
      return (b.priority || 0) - (a.priority || 0);
    });
  }, [rules]);

  return (
    <div data-testid={DASHBOARD.pricingEngineRoot} className="space-y-8">
      <section>
        <button
          data-testid={DASHBOARD.pricingBackBtn}
          onClick={onBack}
          className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors mb-4"
        >
          ← {t("back") || "Volver"}
        </button>
        <div className="label-overline mb-1">SET / 05 · PRICING</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
          {t("pricing_title")}
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
          {t("pricing_subtitle")}
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT — rules table */}
        <div className="lg:col-span-8 space-y-4">
          <div className="flex items-center justify-between">
            <div className="label-overline">
              {rules.length} regla{rules.length === 1 ? "" : "s"}
            </div>
            <button
              data-testid={DASHBOARD.pricingRuleNewBtn}
              onClick={openNew}
              className="font-display font-bold text-sm bg-black text-white px-4 py-2 hover:bg-[var(--accent-cyan,#0E5FFF)] transition-colors"
            >
              + {t("pricing_new_rule")}
            </button>
          </div>

          <div
            data-testid={DASHBOARD.pricingRulesList}
            className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]"
          >
            {loading && (
              <div className="p-6 text-center text-sm text-[var(--text-secondary)]">
                Cargando...
              </div>
            )}
            {!loading && sortedRules.length === 0 && (
              <div className="p-6 text-center text-sm text-[var(--text-secondary)]">
                {t("pricing_no_rules")}
              </div>
            )}
            {!loading &&
              sortedRules.map((rule) => (
                <div
                  key={rule.id}
                  data-testid={DASHBOARD.pricingRuleRow(rule.id)}
                  className={`p-4 flex flex-wrap items-start gap-3 ${
                    !rule.is_active ? "opacity-50" : ""
                  }`}
                >
                  <div className="flex-1 min-w-[240px] space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold text-base">{rule.name}</span>
                      {rule.is_default && (
                        <span className="font-mono-tech text-[0.6rem] font-bold tracking-wider px-1.5 py-0.5 bg-black text-white">
                          DEFAULT
                        </span>
                      )}
                      <StrategyBadge strategy={rule.strategy} />
                      <span className="font-mono-tech text-[0.6rem] tracking-wider text-[var(--text-secondary)]">
                        {t("pricing_rule_priority").toUpperCase()} {rule.priority || 0}
                      </span>
                      {!rule.is_active && (
                        <span className="font-mono-tech text-[0.6rem] font-bold tracking-wider px-1.5 py-0.5 bg-red-100 text-red-800 border border-red-300">
                          {t("pricing_rule_inactive").toUpperCase()}
                        </span>
                      )}
                    </div>
                    <FilterChips applies_to={rule.applies_to} />
                    {(rule.min_profit != null || rule.max_discount_pct != null) && (
                      <div className="flex gap-3 text-[0.7rem] text-[var(--text-secondary)]">
                        {rule.min_profit != null && (
                          <span>
                            💰 {t("pricing_rule_min_profit")}: ${Number(rule.min_profit).toFixed(2)}
                          </span>
                        )}
                        {rule.max_discount_pct != null && (
                          <span>
                            🛡 {t("pricing_rule_max_discount")}: {rule.max_discount_pct}%
                          </span>
                        )}
                      </div>
                    )}
                    {rule.notes && (
                      <div className="text-[0.7rem] text-[var(--text-secondary)] italic">
                        {rule.notes}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      data-testid={DASHBOARD.pricingRuleEditBtn(rule.id)}
                      onClick={() => openEdit(rule)}
                      className="font-mono-tech text-[0.7rem] border border-[var(--border)] px-2.5 py-1 hover:bg-black hover:text-white transition-colors"
                    >
                      {t("pricing_edit")}
                    </button>
                    <button
                      data-testid={DASHBOARD.pricingRuleToggleBtn(rule.id)}
                      onClick={() => handleToggle(rule)}
                      className="font-mono-tech text-[0.7rem] border border-[var(--border)] px-2.5 py-1 hover:bg-black hover:text-white transition-colors"
                    >
                      {rule.is_active ? "OFF" : "ON"}
                    </button>
                    <button
                      data-testid={DASHBOARD.pricingRuleDeleteBtn(rule.id)}
                      onClick={() => handleDelete(rule)}
                      className="font-mono-tech text-[0.7rem] border border-red-300 text-red-700 px-2.5 py-1 hover:bg-red-600 hover:text-white transition-colors"
                    >
                      {t("pricing_delete")}
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* RIGHT — sticky live simulator */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-24">
            <PricingSimulator meta={meta} rulesVersion={rules.length + "-" + rules.map((r) => r.updated_at || r.id).join("|")} />
          </div>
        </div>
      </div>

      {modalOpen && (
        <PricingRuleFormModal
          open={modalOpen}
          rule={editing}
          meta={meta}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
